import React, { useState, useMemo } from 'react';
import { useAllCompetencies } from '../../api/CompetenciesAPI';

const styles = {
  container: {
    marginBottom: '15px',
  },
  competencyGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px',
    alignItems: 'flex-start',
    marginBottom: '10px',
    padding: '8px',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
  },
  phaseGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px',
    marginRight: '12px',
    marginBottom: '8px',
  },
  competencyDot: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    cursor: 'pointer',
    border: '1px solid #ffffff',
    transition: 'all 0.2s ease',
    position: 'relative',
  },
  competencyDotHover: {
    transform: 'scale(1.5)',
    borderWidth: '2px'
  },
  tooltip: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#333333',
    color: 'white',
    padding: '3px 5px',
    borderRadius: '3px',
    fontSize: '6px',
    fontFamily: 'Poppins, sans-serif',
    whiteSpace: 'nowrap',
    zIndex: 1000,
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
    maxWidth: '120px',
    lineHeight: '1.2',
  },
  tooltipArrow: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '3px solid transparent',
    borderRight: '3px solid transparent',
    borderTop: '3px solid #333333',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
    padding: '6px 8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    border: '1px solid #e0e0e0'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
  },
  legendColor: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  legendText: {
    fontSize: '7px',
    fontFamily: 'Poppins, sans-serif',
  },
  emptyMessage: {
    fontSize: '8px',
    color: '#888888',
    fontStyle: 'italic',
    fontFamily: 'Poppins, sans-serif',
    textAlign: 'center',
    padding: '10px',
  }
};

const categoryColors = {
  skills: '#4CAF50',      
  knowledge: '#2196F3',   
  mindset: '#FF9800',     
  instruments: '#9C27B0', 
};

const fillColors = {
  session1Only: '#EC4899',    
  session2Only: '#06B6D4',    
  bothSessions: '#8B5CF6',    
  neither: '#bbb'          
};

const ComparisonCinemaMap = ({ session1Responses = [], session2Responses = [] }) => {
  const [hoveredCompetency, setHoveredCompetency] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Fetch all competencies from the system
  const { data: allCompetenciesData, isLoading, error } = useAllCompetencies();

  // Get competency IDs that were selected in each session
  const session1CompetencyIds = useMemo(() => {
    return new Set(session1Responses?.map(response => response.competency_id) || []);
  }, [session1Responses]);

  const session2CompetencyIds = useMemo(() => {
    return new Set(session2Responses?.map(response => response.competency_id) || []);
  }, [session2Responses]);

  // Process all competencies and group by phase
  const competenciesByPhase = useMemo(() => {
    if (!allCompetenciesData) return {};

    const phases = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];
    const grouped = {};

    // Initialize each phase
    phases.forEach(phase => {
      grouped[phase] = [];
    });

    // Group competencies by phase
    allCompetenciesData.forEach(comp => {
      const phase = comp.abilities?.phase || 'Unknown';
      
      // Determine selection status
      const inSession1 = session1CompetencyIds.has(comp.id);
      const inSession2 = session2CompetencyIds.has(comp.id);
      
      let selectionStatus;
      if (inSession1 && inSession2) {
        selectionStatus = 'bothSessions';
      } else if (inSession1) {
        selectionStatus = 'session1Only';
      } else if (inSession2) {
        selectionStatus = 'session2Only';
      } else {
        selectionStatus = 'neither';
      }

      const competency = {
        id: comp.id,
        name: comp.name,
        category: comp.category,
        phase: phase,
        ability: comp.abilities?.name || 'Unknown',
        selectionStatus: selectionStatus,
        inSession1: inSession1,
        inSession2: inSession2
      };

      if (grouped[phase]) {
        grouped[phase].push(competency);
      } else {
        // Handle unknown phases
        if (!grouped['Unknown']) grouped['Unknown'] = [];
        grouped['Unknown'].push(competency);
      }
    });

    return grouped;
  }, [allCompetenciesData, session1CompetencyIds, session2CompetencyIds]);

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          Loading all competencies...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          Error loading competencies: {error.message}
        </div>
      </div>
    );
  }

  if (!allCompetenciesData || allCompetenciesData.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyMessage}>
          No competencies available in the system
        </div>
      </div>
    );
  }

  const handleMouseEnter = (competency, event) => {
    setHoveredCompetency(competency);
    setMousePosition({
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleMouseLeave = () => {
    setHoveredCompetency(null);
  };

  const handleMouseMove = (event) => {
    if (hoveredCompetency) {
      setMousePosition({
        x: event.clientX,
        y: event.clientY
      });
    }
  };

  const getStatusText = (competency) => {
    switch (competency.selectionStatus) {
      case 'bothSessions':
        return 'Both sessions';
      case 'session1Only':
        return 'Session 1 only';
      case 'session2Only':
        return 'Session 2 only';
      case 'neither':
        return 'Neither session';
      default:
        return 'Unknown';
    }
  };

  return (
    <div style={styles.container}>
      {/* Competencies grouped by phases with spacing */}
      <div style={styles.competencyGrid}>
        {Object.entries(competenciesByPhase).map(([phase, competencies]) => {
          if (!competencies || competencies.length === 0) return null;
          
          return (
            <div key={phase} style={styles.phaseGroup}>
              {competencies.map((competency, index) => {
                const categoryColor = categoryColors[competency.category] || '#565656';
                const fillColor = fillColors[competency.selectionStatus] || '#565656';
                
                return (
                  <div
                    key={`${competency.id}-${index}`}
                    style={{
                      ...styles.competencyDot,
                      backgroundColor: fillColor,
                      borderColor: categoryColor,
                      borderWidth: '0px',
                      ...(hoveredCompetency?.id === competency.id ? styles.competencyDotHover : {})
                    }}
                    onMouseEnter={(e) => handleMouseEnter(competency, e)}
                    onMouseLeave={handleMouseLeave}
                    onMouseMove={handleMouseMove}
                    title={competency.name}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Floating tooltip */}
      {hoveredCompetency && (
        <div
          style={{
            position: 'fixed',
            left: mousePosition.x - 80,
            top: mousePosition.y - 55,
            backgroundColor: '#333333',
            color: 'white',
            padding: '4px 6px',
            borderRadius: '4px',
            fontSize: '7px',
            fontFamily: 'Poppins, sans-serif',
            zIndex: 1000,
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
            maxWidth: '140px',
            lineHeight: '1.3',
          }}
        >
          <div>{hoveredCompetency.name}</div>
          <div style={{ fontSize: '6px', opacity: 0.8, marginTop: '1px' }}>
            {hoveredCompetency.category} • {hoveredCompetency.phase}
          </div>
          <div style={{ fontSize: '6px', opacity: 0.8, marginTop: '1px' }}>
            {getStatusText(hoveredCompetency)}
          </div>
          {/* Arrow pointing down */}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '3px solid transparent',
              borderRight: '3px solid transparent',
              borderTop: '3px solid #333333',
            }}
          ></div>
        </div>
      )}

      {/* Compact legend */}
      <div style={styles.legend}>
        {/* Category colors */}
        {Object.entries(categoryColors).map(([category, color]) => (
          <div key={category} style={styles.legendItem}>
            <div style={{
              ...styles.legendColor,
              backgroundColor: 'transparent',
              border: `2px solid ${color}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}></div>
            <span style={{ 
              ...styles.legendText,
              textTransform: 'capitalize'
            }}>
              {category}
            </span>
          </div>
        ))}
        
        {/* Session status indicators */}
        <div style={{ ...styles.legendItem, marginLeft: '10px' }}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: fillColors.session1Only,
            border: '1px solid #ccc',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}></div>
          <span style={styles.legendText}>Session 1</span>
        </div>
        
        <div style={styles.legendItem}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: fillColors.session2Only,
            border: '1px solid #ccc',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}></div>
          <span style={styles.legendText}>Session 2</span>
        </div>

        <div style={styles.legendItem}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: fillColors.bothSessions,
            border: '1px solid #ccc',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}></div>
          <span style={styles.legendText}>Both</span>
        </div>
        
        <div style={styles.legendItem}>
          <div style={{
            ...styles.legendColor,
            backgroundColor: fillColors.neither,
            border: '1px solid #ccc',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
          }}></div>
          <span style={styles.legendText}>Neither</span>
        </div>
      </div>
    </div>
  );
};

export default ComparisonCinemaMap;
